import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, ChevronDown, Eye, FileText, HandCoins, MapPinned, PiggyBank, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { exportSections, logoDataUrl } from "@/lib/exporter";
import { StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { fmtDate, rupiah, todayStr } from "@/lib/format";

const RHEAD = ["Nama Resort", "Anggota", "Simpanan", "Sisa Pinjaman", "Pegawai", "Saldo Kas Harian"];
const THEAD = [...RHEAD, ""];

const ResortRows = ({ c }) => (
  <tr data-testid={`office-resorts-${c.id}`}>
    <td colSpan={7} className="bg-muted/40 px-4 py-3">
      {c.resorts.length === 0 ? <p className="text-sm text-muted-foreground text-center py-2">Belum ada resort.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left text-xs text-muted-foreground">{THEAD.map((h, i) => <th key={h || "aksi"} className={`py-2 px-3 font-medium ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              {c.resorts.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`office-resort-row-${r.id}`}>
                  <td className="py-2 px-3 font-medium"><span className="inline-flex items-center gap-1.5"><MapPinned className="h-3.5 w-3.5 text-primary" />{r.name}</span></td>
                  <td className="py-2 px-3 text-right font-mono">{r.anggota}</td>
                  <td className="py-2 px-3 text-right font-mono">{rupiah(r.simpanan)}</td>
                  <td className="py-2 px-3 text-right font-mono">{rupiah(r.sisa)}</td>
                  <td className="py-2 px-3 text-right font-mono">{r.pegawai}</td>
                  <td className={`py-2 px-3 text-right font-mono ${r.kas_harian < 0 ? "text-rose-500" : ""}`}>{rupiah(r.kas_harian)}</td>
                  <td className="py-2 px-3 text-right"><Button asChild size="sm" variant="outline" className="h-8 rounded-full"><Link to={`/laporan-kantor/resort/${r.id}`} data-testid={`office-resort-view-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Lihat Resort</Link></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </td>
  </tr>
);

export default function OfficeReportPage() {
  const { unit, id } = useParams();
  const navigate = useNavigate();
  const { settings, logoSrc } = useSettings();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({});
  const isWil = unit === "wilayah";
  useEffect(() => {
    api.get(`/overview/${isWil ? "wilayah" : "cabang"}/${id}/report`).then((r) => {
      setD(r.data);
      if (!isWil && r.data.cabang[0]) setOpen({ [r.data.cabang[0].id]: true });
    }).catch((e) => { setErr(errMsg(e)); toast.error(errMsg(e)); });
  }, [unit, id, isWil]);
  if (err) return <div className="py-16 text-center" data-testid="office-report-error"><p className="text-muted-foreground mb-4">{err}</p><Button variant="outline" className="rounded-full" onClick={() => navigate(-1)}>Kembali</Button></div>;
  if (!d) return <p className="text-muted-foreground">Memuat...</p>;
  const pdf = async () => exportSections("pdf", {
    logo: await logoDataUrl(logoSrc),
    title: `${settings.app_name} — Laporan Kantor ${isWil ? "Wilayah" : "Cabang"} ${d.office.name}`,
    subtitle: `Per ${fmtDate(todayStr())} · Anggota ${d.cards.anggota} · Simpanan ${rupiah(d.cards.simpanan)} · Sisa Pinjaman ${rupiah(d.cards.sisa)}`,
    filename: `laporan-${unit}-${d.office.name}-${todayStr()}`,
    sections: d.cabang.map((c) => ({
      title: `${c.name} — Anggota ${c.anggota}, Simpanan ${rupiah(c.simpanan)}, Sisa ${rupiah(c.sisa)}`, head: RHEAD, money: [2, 3, 5],
      rows: [...c.resorts.map((r) => [r.name, r.anggota, r.simpanan, r.sisa, r.pegawai, r.kas_harian]), ["TOTAL", c.anggota, c.simpanan, c.sisa, c.resorts.reduce((s, r) => s + r.pegawai, 0), c.kas_harian]],
    })),
  });
  return (
    <div>
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground mb-3" data-testid="office-report-back"><ArrowLeft className="h-4 w-4 mr-1" />Kembali</button>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Laporan Kantor {isWil ? "Wilayah" : "Cabang"}</p><h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="office-report-title">{d.office.name}</h1></div>
        <Button variant="outline" className="h-10 rounded-full" onClick={pdf} data-testid="office-report-pdf"><FileText className="h-4 w-4 mr-1.5" />Cetak PDF</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Jumlah Anggota" value={d.cards.anggota} icon={Users} testId="office-card-anggota" />
        <StatCard label="Jumlah Simpanan" value={rupiah(d.cards.simpanan)} icon={PiggyBank} testId="office-card-simpanan" />
        <StatCard label="Sisa Pinjaman" value={rupiah(d.cards.sisa)} icon={HandCoins} testId="office-card-sisa" />
        <StatCard label={isWil ? "Jumlah Kantor Cabang" : "Jumlah Resort"} value={isWil ? d.cards.cabang : d.cards.resort} icon={isWil ? Building2 : MapPinned} testId="office-card-count" />
      </div>
      <div className="panel !p-0 overflow-x-auto" data-testid="office-report-table">
        <table className="w-full text-sm min-w-[680px]">
          <thead><tr className="text-left text-xs text-muted-foreground border-b">
            <th className="py-3 pl-5 font-medium">Kantor Cabang</th><th className="py-3 px-3 font-medium text-right">Resort</th><th className="py-3 px-3 font-medium text-right">Anggota</th>
            <th className="py-3 px-3 font-medium text-right">Simpanan</th><th className="py-3 px-3 font-medium text-right">Sisa Pinjaman</th><th className="py-3 px-3 font-medium text-right">Pegawai</th><th className="py-3 pr-5 font-medium text-right">Saldo Kas Harian</th>
          </tr></thead>
          <tbody>
            {d.cabang.map((c) => (
              <Fragment key={c.id}>
                <tr className="border-b cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => setOpen({ ...open, [c.id]: !open[c.id] })} data-testid={`office-cabang-row-${c.id}`}>
                  <td className="py-3 pl-5 font-medium"><span className="inline-flex items-center gap-2"><ChevronDown className={`h-4 w-4 transition-transform ${open[c.id] ? "rotate-180" : ""}`} />{c.name}</span></td>
                  <td className="py-3 px-3 text-right font-mono">{c.resorts.length}</td>
                  <td className="py-3 px-3 text-right font-mono">{c.anggota}</td>
                  <td className="py-3 px-3 text-right font-mono">{rupiah(c.simpanan)}</td>
                  <td className="py-3 px-3 text-right font-mono text-primary">{rupiah(c.sisa)}</td>
                  <td className="py-3 px-3 text-right font-mono">{c.pegawai}</td>
                  <td className={`py-3 pr-5 text-right font-mono ${c.kas_harian < 0 ? "text-rose-500" : ""}`}>{rupiah(c.kas_harian)}</td>
                </tr>
                {open[c.id] && <ResortRows c={c} />}
              </Fragment>
            ))}
            {d.cabang.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">Belum ada kantor cabang.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
